import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../core/context/AuthContext";
import { initiateGoogleAuth } from "../../core/auth/google/googleAuth";
import LoggedInCTAButtons from "../../core/components/LoggedInCTAButtons";
import { GoogleIcon, ArrowRightIcon } from "../../design-system/icons";
import { APP_DOMAIN } from "../../core/utils/config";
import { submitPotentialCustomerLead } from "../../core/utils/leadsApi";
import { apiFetch } from "../../core/utils/api";

import LandingNavbar from "../landing/LandingNavbar";
import Testimonials from "../landing/Testimonials";
import FinalCTA from "../landing/FinalCTA";
import Footer from "../landing/Footer";
import useGlobalAccessGrantActive from "../landing/useGlobalAccessGrantActive";
import Website from "../../renderer/pages/Website";
import PreviewContext from "../../editor/context/PreviewContext";

import "./landing_v3.css";

const parseFeaturedWebsiteRoute = (route) => {
  const [username = "", domain = ""] = String(route || "")
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean);

  return { username, domain };
};

const buildPreviewWebsite = (website, index) => {
  const { username, domain } = parseFeaturedWebsiteRoute(website?.url);
  if (!username || !domain) return null;

  return {
    ...website,
    previewKey: `${username}/${domain}-${index}`,
    username,
    domain,
    title: String(website?.name || "").trim() || `${username}/${domain}`,
  };
};

const LiveWebsitePreviewV3 = ({ website }) => {
  const previewContextValue = {
    mode: "responsive",
    viewport: null,
    containerWidth: 1440,
    contentWidth: 1440,
    onNavigate: () => {},
    showFrame: false,
    manualZoom: 1,
  };

  return (
    <div className="landing-v3-iframe-wrapper">
      <PreviewContext.Provider value={previewContextValue}>
        <div style={{ width: "1440px", height: "900px", transform: "scale(0.277)", transformOrigin: "top left" }}>
          <Website username={website.username} domain={website.domain} subdomain="" />
        </div>
      </PreviewContext.Provider>
    </div>
  );
};

const LandingHeroV3 = ({ isFreeGrantActive }) => {
  const [email, setEmail] = useState("");
  const { auth, isHydrated } = useContext(AuthContext);
  const navigate = useNavigate();
  const landingLeadSituation = "LANDING_EMAIL";
  
  const [websites, setWebsites] = useState([]);

  useEffect(() => {
    const fetchPublicWebsites = async () => {
      try {
        const response = await apiFetch("/api/websites/public/featured/");
        if (response.ok) {
          const data = await response.json();
          const parsed = (Array.isArray(data) ? data : [])
            .map((w, i) => buildPreviewWebsite(w, i))
            .filter(Boolean);
          setWebsites(parsed);
        }
      } catch (err) {
        console.warn("Failed to fetch featured public websites", err);
      }
    };
    fetchPublicWebsites();
  }, []);

  const handleCreateWebsite = () => {
    const trimmedEmail = (email || "").trim();
    const isValid = trimmedEmail.length > 0 && trimmedEmail.includes("@");
    if (isValid) {
      void submitPotentialCustomerLead(
        { email: trimmedEmail, situation: landingLeadSituation },
        { keepalive: true }
      ).catch((err) => {
        console.error("Failed to submit potential customer lead:", err);
      });

      const base = `/edit/register?email=${encodeURIComponent(trimmedEmail)}`;
      navigate(base);
    }
  };

  const handleSocialLogin = () => {
    initiateGoogleAuth('register');
  };

  const isEmailValid = email.trim().length > 0 && email.includes("@");

  // Duplicate websites for seamless marquee
  const marqueeItems = [...websites, ...websites, ...websites].filter(Boolean);

  return (
    <div className="landing-v3-container">
      <LandingNavbar />
      
      <div className="landing-v3-hero pt-24">
        <h1 className="landing-v3-title">
          Your portfolio,<br />
          elevated.
        </h1>
        
        <p className="landing-v3-subtitle">
          Create a stunning personal website that makes you impossible to ignore.
          {isFreeGrantActive ? (
            <span className="text-green-600 dark:text-green-400 font-bold block mt-2 transition-colors duration-300">Start for free today.</span>
          ) : null}
        </p>

        <div className="landing-v3-cta-wrapper">
          {!isHydrated ? (
            <div className="h-32" />
          ) : auth && auth.userId ? (
            <LoggedInCTAButtons username={auth.username} />
          ) : (
            <>
              <form
                className="landing-v3-input-group"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!isEmailValid) return;
                  handleCreateWebsite();
                }}
              >
                <input
                  type="email"
                  aria-label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="landing-v3-input"
                />
                <button
                  type="submit"
                  disabled={!isEmailValid}
                  className="landing-v3-btn"
                >
                  Start <ArrowRightIcon size="sm" />
                </button>
              </form>

              <div className="landing-v3-social">
                <div className="landing-v3-divider">or</div>
                <button
                  type="button"
                  onClick={handleSocialLogin}
                  className="landing-v3-google-btn"
                >
                  <GoogleIcon size="sm" />
                  Sign up with Google
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {marqueeItems.length > 0 && (
        <div className="landing-v3-marquee-container">
          <div className="landing-v3-marquee-track">
            {marqueeItems.map((website, i) => (
              <div key={`${website.previewKey}-${i}`} className="landing-v3-preview-card">
                <LiveWebsitePreviewV3 website={website} />
                <div className="landing-v3-card-overlay">
                  <h3 className="landing-v3-card-title">{website.title}</h3>
                  <p className="landing-v3-card-domain">{website.domain}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-dark-950 relative z-10 transition-colors duration-300">
        <Testimonials />
        <FinalCTA isFreeGrantActive={isFreeGrantActive} />
        <Footer />
      </div>
    </div>
  );
};

const LandingV3 = () => {
  const isFreeGrantActive = useGlobalAccessGrantActive();

  return <LandingHeroV3 isFreeGrantActive={isFreeGrantActive} />;
};

export default LandingV3;
